<template>
  <v-dialog
    v-model="dialog"
    max-width="480"
    @click:outside="cancel"
  >
    <v-card light>
      <v-card-title> Settings </v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-text-field
            v-model.number="settings.maxEmailsPerWeek"
            type="number"
            min="0"
            label="Max emails per week (per work area)"
            hint="Counted per targeted industry/topic. A send-to-everyone email counts toward every work area. Any number of filtered emails may share a day — they reach different audiences; only a send-to-everyone email takes a day alone."
            persistent-hint
            outlined
          />
          <v-text-field
            v-model.number="settings.maxSocialPostsPerWeek"
            type="number"
            min="0"
            label="Max social posts per week"
            outlined
          />
          <v-text-field
            v-model.number="settings.seasonalLeadDays"
            type="number"
            min="0"
            label="Seasonal lead days"
            outlined
          />
          <v-text-field
            v-model.number="settings.maxImagesPerPost"
            type="number"
            min="1"
            label="Images per Instagram post"
            hint="X, Pinterest, Facebook and LinkedIn always use a single image."
            persistent-hint
            outlined
          />
          <div class="text-subtitle-2 mb-1 mt-2">Enabled social networks</div>
          <v-checkbox
            v-for="p in platformOptions"
            :key="p.id"
            :input-value="isPlatformEnabled(p.id)"
            :label="p.name"
            dense
            hide-details
            class="mt-0"
            @change="togglePlatform(p.id)"
          />

          <v-divider class="my-3" />
          <div class="text-subtitle-2 mb-1">Pinterest boards</div>
          <div
            v-if="!pinterest.configured"
            class="text-caption grey--text mb-2"
          >
            Set PinterestClientId, PinterestClientSecret and PinterestRedirectUri in App Settings to
            enable automatic board organization.
          </div>
          <div
            v-else-if="pinterest.connected"
            class="d-flex align-center flex-wrap mb-2"
          >
            <v-icon
              small
              color="success"
              class="mr-1"
            >
              mdi-check-circle
            </v-icon>
            <span class="text-caption mr-2"
              >Connected — a board is created per industry/topic on send.</span
            >
            <v-btn
              text
              x-small
              :loading="pinterestLoading"
              @click="disconnectPinterest"
            >
              Disconnect
            </v-btn>
          </div>
          <div
            v-else
            class="mb-2"
          >
            <v-btn
              small
              outlined
              color="mcsprimary"
              :loading="pinterestLoading"
              @click="connectPinterest"
            >
              Connect Pinterest
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="mcsprimary"
          :loading="saving"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'Settings',
  data: () => ({
    dialog: false,
    saving: false,
    valid: false,
    pinterestLoading: false,
    pinterest: { configured: false, connected: false },
    settings: {
      maxEmailsPerWeek: 0,
      maxSocialPostsPerWeek: 0,
      seasonalLeadDays: 0,
      maxImagesPerPost: 3,
      enabledPlatforms: []
    }
  }),
  computed: {
    platformOptions() {
      return Object.entries(this.$enums.MARKETING.PLATFORM).map(([id, name]) => ({
        id: Number(id),
        name
      }));
    }
  },
  methods: {
    open() {
      this.dialog = true;
      this.saving = false;
      this.$store
        .dispatch('campaigns/getSettings')
        .then((data) => {
          if (data) {
            this.settings = { enabledPlatforms: [], ...data };
          }
        })
        .catch((error) => {
          this.logError(error, 'Failed loading settings');
        });
      this.loadPinterestStatus();
    },
    cancel() {
      this.dialog = false;
    },
    loadPinterestStatus() {
      return this.$store
        .dispatch('campaigns/pinterestStatus')
        .then((data) => {
          if (data) this.pinterest = data;
        })
        .catch((error) => this.logError(error));
    },
    connectPinterest() {
      this.pinterestLoading = true;
      this.$store
        .dispatch('campaigns/pinterestAuthUrl')
        .then((data) => {
          if (data && data.url) window.location.href = data.url;
        })
        .catch((error) => this.logError(error, 'Could not start Pinterest connect'))
        .finally(() => {
          this.pinterestLoading = false;
        });
    },
    disconnectPinterest() {
      this.pinterestLoading = true;
      this.$store
        .dispatch('campaigns/pinterestDisconnect')
        .then(() => this.loadPinterestStatus())
        .catch((error) => this.logError(error, 'Could not disconnect Pinterest'))
        .finally(() => {
          this.pinterestLoading = false;
        });
    },
    isPlatformEnabled(id) {
      return (this.settings.enabledPlatforms || []).indexOf(id) !== -1;
    },
    togglePlatform(id) {
      const list = this.settings.enabledPlatforms || [];
      const idx = list.indexOf(id);
      if (idx === -1) {
        this.settings.enabledPlatforms = [...list, id];
      } else {
        this.settings.enabledPlatforms = list.filter((p) => p !== id);
      }
    },
    save() {
      this.saving = true;
      this.$store
        .dispatch('campaigns/saveSettings', this.settings)
        .then(() => {
          this.$root.$snackbarSuccess('Settings saved');
          this.dialog = false;
          this.$emit('saved');
        })
        .catch((error) => {
          this.logError(error, 'Failed saving settings');
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>
